class AsAboutPage extends HTMLElement {
  constructor() {
    super();

    this.storeSelectors = [];
    this.storeDetails = [];
    this.storePanels = [];
    this.storeContainer = null;
    this.materialComponents = [];
    this.expandedStoreIdentifier = null;
  }

  connectedCallback() {
    this.materialComponents = Array.from(this.querySelectorAll('[data-material-component]'));
    this.storeSelectors = Array.from(this.querySelectorAll('[data-store-selector]'));
    this.storeDetails = Array.from(this.querySelectorAll('[data-store-detail]'));
    this.storePanels = Array.from(this.querySelectorAll('[data-store-panel]'));
    this.storeContainer = this.querySelector('.as-about-stores');
    this.storeGalleries = this.querySelector('.as-about-stores__galleries');

    this.initializeMaterialComponents();
    this.initializeStoreGalleryHeight();

    if (this.storeSelectors.length > 0) {
      this.storeSelectors.forEach((storeSelector) => {
        storeSelector.addEventListener('mouseenter', () => {
          this.previewStore(storeSelector.dataset.storeSelector);
        });
        storeSelector.addEventListener('focus', () => {
          this.previewStore(storeSelector.dataset.storeSelector);
        });
        storeSelector.addEventListener('click', () => {
          this.expandStore(storeSelector.dataset.storeSelector, false);
        });
        storeSelector.addEventListener('keydown', (event) => this.handleStoreKeydown(event));

      });

      this.storeDetails.forEach((storeDetail) => {
        const backControl = storeDetail.querySelector('[data-store-back]');

        backControl?.addEventListener('click', (event) => {
          event.preventDefault();
          event.stopPropagation();
          this.collapseStore(true);
        });
        backControl?.addEventListener('keydown', (event) => {
          if (event.key !== 'Enter' && event.key !== ' ') {
            return;
          }

          event.preventDefault();
          event.stopPropagation();
          this.collapseStore(true);
        });
      });

      this.activateStore('0', false);
    }
  }

  initializeStoreGalleryHeight() {
    if (!this.storeGalleries) {
      return;
    }

    const images = Array.from(this.storeGalleries.querySelectorAll('.as-about-store-gallery__image'));
    const updateHeight = () => {
      const galleryWidth = this.storeGalleries.clientWidth;
      if (!galleryWidth) {
        return;
      }

      const loadedImages = images.filter((image) => image.naturalWidth && image.naturalHeight);
      if (!loadedImages.length) {
        return;
      }

      const maxHeight = Math.max(
        ...loadedImages.map((image) => galleryWidth * image.naturalHeight / image.naturalWidth),
      );
      this.storeGalleries.style.setProperty('--as-about-store-gallery-height', `${maxHeight}px`);
    };

    images.forEach((image) => image.addEventListener('load', updateHeight, { once: false }));
    updateHeight();

    if ('ResizeObserver' in window) {
      this.storeGalleryResizeObserver = new ResizeObserver(updateHeight);
      this.storeGalleryResizeObserver.observe(this.storeGalleries);
    }
  }

  initializeMaterialComponents() {
    this.materialComponents.forEach((materialComponent) => {
      materialComponent.materialPanels = Array.from(materialComponent.querySelectorAll('[data-material-panel]'));
      materialComponent.materialTriggers = Array.from(materialComponent.querySelectorAll('[data-material-trigger]'));
      materialComponent.backButtons = Array.from(materialComponent.querySelectorAll('[data-material-back]'));
      materialComponent.lastTrigger = null;

      materialComponent.materialTriggers.forEach((materialTrigger) => {
        materialTrigger.addEventListener('click', () => {
          materialComponent.lastTrigger = materialTrigger;
          this.activateMaterialPanel(materialComponent, materialTrigger.dataset.materialTrigger, true);
        });
      });

      materialComponent.backButtons.forEach((backButton) => {
        backButton.addEventListener('click', () => {
          this.activateMaterialPanel(materialComponent, 'overview', false);

          if (materialComponent.lastTrigger) {
            materialComponent.lastTrigger.focus({ preventScroll: true });
          }
        });
      });

      this.activateMaterialPanel(materialComponent, 'overview', false);
    });
  }

  activateMaterialPanel(materialComponent, panelIdentifier, moveFocus) {
    const selectedPanel = materialComponent.materialPanels.find(
      (materialPanel) => materialPanel.dataset.materialPanel === panelIdentifier,
    );

    if (!selectedPanel) {
      return;
    }

    materialComponent.dataset.materialState = panelIdentifier;

    materialComponent.materialPanels.forEach((materialPanel) => {
      const isSelected = materialPanel === selectedPanel;
      materialPanel.hidden = !isSelected;
      materialPanel.setAttribute('aria-hidden', String(!isSelected));
    });

    materialComponent.materialTriggers.forEach((materialTrigger) => {
      const controlsSelectedPanel = materialTrigger.getAttribute('aria-controls') === selectedPanel.id;
      materialTrigger.setAttribute('aria-expanded', String(controlsSelectedPanel));
    });

    if (moveFocus && panelIdentifier !== 'overview') {
      const focusTarget = selectedPanel.querySelector('[data-material-panel-heading], [data-material-back]');
      focusTarget?.focus({ preventScroll: true });
    }
  }

  activateStore(storeIdentifier, focusSelector) {
    const normalizedStoreIdentifier = String(storeIdentifier);
    const selectedStore = this.storeSelectors.find(
      (storeSelector) => storeSelector.dataset.storeSelector === normalizedStoreIdentifier,
    );

    if (!selectedStore) {
      return;
    }

    this.storeSelectors.forEach((storeSelector) => {
      const isSelected = storeSelector.dataset.storeSelector === normalizedStoreIdentifier;
      storeSelector.setAttribute('aria-selected', String(isSelected));
      storeSelector.tabIndex = isSelected ? 0 : -1;
    });

    this.storeDetails.forEach((storeDetail) => {
      storeDetail.hidden =
        this.expandedStoreIdentifier === null || storeDetail.dataset.storeDetail !== normalizedStoreIdentifier;
    });

    this.storePanels.forEach((storePanel) => {
      storePanel.hidden = storePanel.dataset.storePanel !== normalizedStoreIdentifier;
    });

    if (focusSelector) {
      selectedStore.focus({ preventScroll: true });
    }

  }

  previewStore(storeIdentifier) {
    if (this.expandedStoreIdentifier !== null || window.matchMedia('(max-width: 767px)').matches) {
      return;
    }

    this.activateStore(storeIdentifier, false);
  }

  expandStore(storeIdentifier, focusSelector) {
    if (window.matchMedia('(max-width: 767px)').matches) {
      return;
    }

    const normalizedStoreIdentifier = String(storeIdentifier);
    const selectedStore = this.storeSelectors.find(
      (storeSelector) => storeSelector.dataset.storeSelector === normalizedStoreIdentifier,
    );

    if (!selectedStore) {
      return;
    }

    this.expandedStoreIdentifier = normalizedStoreIdentifier;
    this.storeContainer?.setAttribute('data-store-expanded', 'true');
    this.storeSelectors.forEach((storeSelector) => {
      storeSelector.hidden = true;
      storeSelector.setAttribute('aria-selected', String(storeSelector === selectedStore));
      storeSelector.tabIndex = -1;
      storeSelector.dataset.storeExpanded = 'true';
    });
    this.storeDetails.forEach((storeDetail) => {
      storeDetail.hidden = storeDetail.dataset.storeDetail !== normalizedStoreIdentifier;
    });
    this.activateStore(normalizedStoreIdentifier, focusSelector);
  }

  collapseStore(moveFocus) {
    if (this.expandedStoreIdentifier === null) {
      return;
    }

    const previouslyExpandedSelector = this.storeSelectors.find(
      (storeSelector) => storeSelector.dataset.storeSelector === this.expandedStoreIdentifier,
    );

    this.expandedStoreIdentifier = null;
    this.storeContainer?.removeAttribute('data-store-expanded');

    this.storeDetails.forEach((storeDetail) => {
      storeDetail.hidden = true;
    });

    this.storeSelectors.forEach((storeSelector) => {
      storeSelector.hidden = false;
      storeSelector.removeAttribute('data-store-expanded');
      storeSelector.tabIndex = storeSelector.dataset.storeSelector === '0' ? 0 : -1;
    });

    this.activateStore('0', false);

    if (moveFocus) {
      previouslyExpandedSelector?.focus({ preventScroll: true });
    }
  }

  handleStoreKeydown(event) {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      this.expandStore(event.currentTarget.dataset.storeSelector, false);
      return;
    }

    const currentStoreIndex = this.storeSelectors.indexOf(event.currentTarget);
    let nextStoreIndex = currentStoreIndex;

    if (event.key === 'ArrowDown' || event.key === 'ArrowRight') {
      nextStoreIndex = (currentStoreIndex + 1) % this.storeSelectors.length;
    } else if (event.key === 'ArrowUp' || event.key === 'ArrowLeft') {
      nextStoreIndex = (currentStoreIndex - 1 + this.storeSelectors.length) % this.storeSelectors.length;
    } else if (event.key === 'Home') {
      nextStoreIndex = 0;
    } else if (event.key === 'End') {
      nextStoreIndex = this.storeSelectors.length - 1;
    } else {
      return;
    }

    event.preventDefault();
    this.activateStore(this.storeSelectors[nextStoreIndex].dataset.storeSelector, true);
  }
}

if (!customElements.get('as-about-page')) {
  customElements.define('as-about-page', AsAboutPage);
}
